(() => {
  'use strict';
  if (document.documentElement.dataset.houseGolden !== 'on') return;
  const PHI = (1 + Math.sqrt(5)) / 2;
  const selector = '.gg-card__media > img.gg-card__img, .gg-card__model > img.gg-card__model-still, .gg-pdp__slide.gg-plate > img, .srlm-featured__item > img, .srlm-narr__pic > img, .gg-search__pillar-image > img';
  const cache = new Map(), states = new WeakMap();
  const valid = b => b && b.length === 4 && b.every(Number.isFinite) && b[0] >= 0 && b[1] >= 0 && b[2] <= 1 && b[3] <= 1 && b[2] > b[0] && b[3] > b[1];
  const identity = src => {
    try {
      const url = new URL(src, location.href);
      if (!/^https?:$/.test(url.protocol)) return src;
      // Shopify renditions of the same file share a framing, but a new file/version never does.
      const path = url.pathname.replace(/^.*\/(?:files|products)\//, '/');
      const params = new URLSearchParams(url.search);
      ['width','height','format','quality','pad_color'].forEach(k=>params.delete(k));
      params.sort();
      return path + '?' + params.toString();
    } catch { return src || ''; }
  };
  const fingerprint = src => {
    let a=2166136261,b=2246822507;
    for(const c of identity(src)){a=Math.imul(a^c.charCodeAt(0),16777619);b=Math.imul(b^c.charCodeAt(0),3266489909);}
    return (a>>>0).toString(36)+'-'+(b>>>0).toString(36);
  };
  const sourceOf = img => {
    const shown=img.currentSrc||img.src||'';
    return shown.startsWith('blob:') && img.dataset.gradeSrc ? img.dataset.gradeSrc : shown;
  };
  const mediaSource = media => {
    const model=media.matches?.('gg-model-viewer')?media:media.querySelector('gg-model-viewer');
    if(model?.getAttribute('src'))return model.getAttribute('src');
    const img=media.querySelector('img:not(.gg-model-viewer__still)');
    return img?sourceOf(img):'';
  };
  const key = (scope,src) => 'golden-'+(scope==='master'?'all':scope||'all')+'-'+src;
  const manual = media => {
    const src=fingerprint(mediaSource(media));
    let live,doc;
    try{live=JSON.parse(media.dataset.goldenEdit||'null');doc=JSON.parse(media.dataset.plate||'null');}catch{}
    if(live?.goldenSource===src)return live;
    const rack=media.dataset.plateRack||media.dataset.pdpFrame;
    const record=doc?.racks?.[key(rack,src)]||doc?.racks?.[key('master',src)];
    return record?{...record,goldenSource:src}:null;
  };
  // Keep the existing grade/media channel separate from image-bound geometry.
  // The API replaces a rack record, so every write preserves its unrelated fields.
  const writes = (media,{productId,handle,scope,rack,frame:f}) => {
    let doc={};try{doc=JSON.parse(media?.dataset.plate||'{}');}catch{}
    const out=[],all=scope==='master';
    const put=(scope,rack,plate)=>out.push({productId,...(handle?{handle}:{}),scope,rack,plate,clear:false});
    if(all)for(const name of Object.keys(doc.racks||{})){
      if(name.startsWith('golden-')&&name.endsWith('-'+f.goldenSource))put('rack',name,null);
    }
    const legacy={...(all?doc.master:doc.racks?.[rack]),grain:f.grain};
    delete legacy.grade;if(f.grade)legacy.grade=f.grade;
    if(!all&&f.media)legacy.media=f.media;
    put(all?'master':'rack',rack,legacy);
    if(all){
      for(const [name,record]of Object.entries(doc.racks||{})){
        if(name.startsWith('golden-')||(!('grade'in record)&&!('grain'in record)))continue;
        const next={...record};delete next.grade;delete next.grain;
        if(name===rack&&f.media)next.media=f.media;
        put('rack',name,Object.keys(next).length?next:null);
      }
      if(f.media&&!('grade'in(doc.racks?.[rack]||{}))&&!('grain'in(doc.racks?.[rack]||{})))put('rack',rack,{...doc.racks?.[rack],media:f.media});
    }
    put('rack',key(all?'master':rack,f.goldenSource),{s:f.s,x:f.x,y:f.y,r:f.r||0,flipX:!!f.flipX,flipY:!!f.flipY});
    return out;
  };
  const geometry = (w, h, nw, nh, b, category='other', detail=false) => {
    const ow=nw*(b[2]-b[0]),oh=nh*(b[3]-b[1]);
    const plan=window.ggProductFraming?.fit(w,h,ow,oh,category,b.metrics,detail);
    const scale = plan?.scale ?? Math.min(w/(PHI*ow),h/(PHI*oh));
    const width = nw * scale, height = nh * scale;
    return { width, height, left: w / 2 - width * (b[0] + b[2]) / 2, top: h / 2 - height * (b[1] + b[3]) / 2 };
  };
  const bounds = (im, artwork = false) => {
    const canvas = document.createElement('canvas');
    const k=Math.min(1,640/Math.max(im.naturalWidth,im.naturalHeight));
    canvas.width=Math.max(1,Math.round(im.naturalWidth*k));canvas.height=Math.max(1,Math.round(im.naturalHeight*k));
    const ctx=canvas.getContext('2d',{willReadFrequently:true});
    ctx.drawImage(im,0,0,canvas.width,canvas.height);
    return pixelBounds(ctx.getImageData(0,0,canvas.width,canvas.height).data,canvas.width,canvas.height,artwork);
  };
  function pixelBounds(d,w,h,artwork=false) {
    const edge=[],channels=[[],[],[]],band=Math.max(1,Math.round(Math.min(w,h)*.01));
    let transparent=0;
    for(let y=0;y<h;y++)for(let x=0;x<w;x++)if(x<band||x>=w-band||y<band||y>=h-band){
      const p=(y*w+x)*4;edge.push(p);if(d[p+3]<8)transparent++;
      for(let c=0;c<3;c++)channels[c].push(d[p+c]);
    }
    const alpha=transparent/edge.length>.9;
    const bg=channels.map(a=>{a.sort((a,b)=>a-b);return a[a.length>>1];});
    const difference=p=>Math.max(Math.abs(d[p]-bg[0]),Math.abs(d[p+1]-bg[1]),Math.abs(d[p+2]-bg[2]));
    if(!alpha&&edge.filter(p=>difference(p)<18).length/edge.length<.94){
      // A full-bleed artwork is itself the rectangle; other photographs are left untouched.
      return artwork&&transparent===0?[0,0,1,1]:null;
    }
    const marks=new Uint8Array(w*h),seen=new Uint8Array(w*h),queue=new Int32Array(w*h),components=[];
    for(let i=0;i<marks.length;i++){
      const p=i*4,delta=difference(p);
      if(alpha?d[p+3]>12:d[p+3]>16&&delta>6)marks[i]=(alpha?d[p+3]>96:delta>20)?2:1;
    }
    for(let i=0;i<marks.length;i++){
      if(!marks[i]||seen[i])continue;
      let read=0,end=1,seed=0,contrast=0,x0=w,y0=h,x1=0,y1=0;queue[0]=i;seen[i]=1;
      while(read<end){
        const n=queue[read++],x=n%w,y=Math.floor(n/w);seed+=marks[n]===2?1:0;
        contrast+=difference(n*4)/255;
        x0=Math.min(x0,x);x1=Math.max(x1,x);y0=Math.min(y0,y);y1=Math.max(y1,y);
        for(let dy=-1;dy<=1;dy++)for(let dx=-1;dx<=1;dx++){
          const xx=x+dx,yy=y+dy;if(xx<0||xx>=w||yy<0||yy>=h)continue;
          const next=yy*w+xx;if(marks[next]&&!seen[next]){seen[next]=1;queue[end++]=next;}
        }
      }
      components.push({size:end,seed,contrast,x0,x1,y0,y1});
    }
    const largest=components.reduce((max,c)=>Math.max(max,c.size),0);
    const keep=components.filter(c=>c.seed>=2&&c.size>=Math.max(4,largest*.0005));
    if(!keep.length)return null;
    const x0=Math.min(...keep.map(c=>c.x0)),x1=Math.max(...keep.map(c=>c.x1)),y0=Math.min(...keep.map(c=>c.y0)),y1=Math.max(...keep.map(c=>c.y1));
    if(x1<=x0||y1<=y0)return null;
    const result=[Math.max(0,x0-1)/w,Math.max(0,y0-1)/h,Math.min(w,x1+2)/w,Math.min(h,y1+2)/h];
    const mass=keep.reduce((sum,c)=>sum+c.size,0);
    result.metrics={coverage:mass/((x1-x0+1)*(y1-y0+1)),contrast:keep.reduce((sum,c)=>sum+c.contrast,0)/mass};
    return result;
  }
  const measure = (src, artwork) => {
    const id=identity(src)+(artwork?'#artwork':'');
    if(cache.has(id))return cache.get(id);
    const url=new URL(src,location.href);
    if(!url.searchParams.has('crop')){url.searchParams.delete('height');url.searchParams.set('width','640');}
    const pending=new Promise(resolve=>{
      const im=new Image();im.crossOrigin='anonymous';
      im.onload=()=>{try{resolve(bounds(im,artwork));}catch{resolve(null);}};
      im.onerror=()=>resolve(null);im.src=url.href;
    });
    cache.set(id,pending);
    if(cache.size>160)cache.delete(cache.keys().next().value);
    return pending;
  };
  const place = img => {
    const state=states.get(img);
    if(!state?.box||!img.naturalWidth||!img.isConnected)return;
    const frame=img.parentElement,plate=img.closest('.gg-plate')||frame;
    const info=frame.querySelector(':scope > .srlm-featured__item-info');
    const w=frame.clientWidth,h=frame.clientHeight-(info?.offsetHeight||0);
    if(!w||!h)return;
    const context=window.ggProductFraming?.describe(plate)||{};
    const f=manual(plate),g=geometry(w,h,img.naturalWidth,img.naturalHeight,state.box,context.category,context.detail);
    const factor=f?.s??1;
    g.width*=factor;g.height*=factor;
    g.left=w/2-g.width*(state.box[0]+state.box[2])/2+(f?.x||0)*w/100;
    g.top=h/2-g.height*(state.box[1]+state.box[3])/2+(f?.y||0)*h/100;
    for(const [name,val]of Object.entries(g))img.style.setProperty('--object-'+name,val+'px');
    img.style.setProperty('--object-transform',`rotate(${f?.r||0}deg) scale(${f?.flipX?-1:1},${f?.flipY?-1:1})`);
    img.style.setProperty('--object-origin',`${(state.box[0]+state.box[2])/2*g.width}px ${(state.box[1]+state.box[3])/2*g.height}px`);
    img.dataset.objectBounds=state.box.join(',');img.dataset.objectFramed='true';img.dataset.objectFraming=f?'manual-current-media':'golden';
    img.dataset.objectProfile=context.category||'other';
    if(state.box.metrics)img.dataset.objectMetrics=JSON.stringify(state.box.metrics);
  };
  const resize=new ResizeObserver(entries=>entries.forEach(({target})=>target.querySelectorAll(':scope > img').forEach(place)));
  const prepare = async img => {
    const src=sourceOf(img);if(!src||src.startsWith('blob:'))return;
    const id=identity(src);
    if(states.get(img)?.src===id){place(img);return;}
    const state={src:id,box:null};states.set(img,state);
    delete img.dataset.objectFramed;delete img.dataset.objectBounds;
    img.dataset.objectMeasurement='measuring';
    const artwork=img.parentElement.hasAttribute('data-golden-print')||window.ggProductFraming?.describe(img).category==='artwork';
    // Measure the current source. Old media-id bounds must not follow a replaced/retouched file.
    const box=await measure(src,artwork);
    if(states.get(img)!==state)return;
    if(!valid(box)){img.dataset.objectMeasurement='unresolved';return;}
    state.box=box;img.dataset.objectMeasurement='pixel-bounds';
    resize.observe(img.parentElement);place(img);
  };
  function refresh(media){media.querySelectorAll(selector).forEach(img=>prepare(img));media.querySelectorAll('gg-model-viewer').forEach(v=>v.fit?.());}
  const edit = (media,next,previous) => {
    const src=fingerprint(mediaSource(media));
    const fields=['s','x','y','r','flipX','flipY'];
    if(previous.goldenSource&&previous.goldenSource!==src)return {...next,s:1,x:0,y:0,r:0,flipX:false,flipY:false,goldenSource:null};
    const changed=fields.some(k=>(next[k]||0)!==(previous[k]||0));
    return {...next,goldenSource:changed?src:next.goldenSource||null};
  };
  function installPlate(){
    const p=window.ggPlate;if(!p||p.goldenAware)return;
    const vars=p.vars,set=p.set;
    p.vars=media=>{const old=vars(media),f=manual(media);return {...old,s:f?.s??1,x:f?.x||0,y:f?.y||0,r:f?.r||0,flipX:!!f?.flipX,flipY:!!f?.flipY,goldenSource:f?.goldenSource||null};};
    p.set=(media,f)=>{set(media,f);if(f.goldenSource)media.dataset.goldenEdit=JSON.stringify(f);else if('goldenSource'in f)delete media.dataset.goldenEdit;refresh(media);};
    p.goldenAware=true;
  }
  window.ggGoldenFrame={identity,fingerprint,key,manual,mediaSource,edit,refresh,writes};
  const scan = root => {
    installPlate();
    root.querySelectorAll(selector).forEach(img=>{
      if(img.complete)prepare(img);
      if(!img.dataset.objectListener){img.dataset.objectListener='true';img.addEventListener('load',()=>prepare(img));}
    });
  };
  scan(document);document.addEventListener('DOMContentLoaded',()=>scan(document),{once:true});
  new MutationObserver(records=>{
    const frames=new Set();
    for(const r of records){
      if(r.type==='attributes'){
        if(r.target.matches(selector))prepare(r.target);
        else if(r.target.matches('.gg-plate'))frames.add(r.target);
      }
      if(r.type==='childList')r.addedNodes.forEach(n=>{if(n.nodeType===1){if(n.matches(selector))prepare(n);scan(n);}});
    }
    frames.forEach(refresh);
  }).observe(document.body,{subtree:true,childList:true,attributes:true,attributeFilter:['src','srcset','data-grade-src','data-plate','data-plate-rack','data-golden-edit']});
})();
